import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders, getToken, isSuperuser } from '../api/auth';
import PageContainer from '../components/PageContainer';

const API_BASE = '/api';

interface Citation {
  title?: string;
  document_id?: number;
  chunk_id?: number;
  source_url?: string;
}

interface FeedbackRow {
  id: number;
  session_id: string;
  message_id: string | null;
  rating: number;
  query: string | null;
  answer: string | null;
  comment: string | null;
  domain: string | null;
  language: string | null;
  intent: string | null;
  confidence: number | null;
  citations: Citation[] | null;
  tool_calls_made: string[] | null;
  tokens_used: number | null;
  finish_reason: string | null;
  duration_ms: number | null;
  created_at: string | null;
}

interface ListResponse {
  items: FeedbackRow[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

const Feedback: React.FC = () => {
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [rating, setRating] = useState<'' | 'up' | 'down'>('');
  const [language, setLanguage] = useState('');
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<FeedbackRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (rating === 'up') params.rating = 1;
      if (rating === 'down') params.rating = -1;
      if (language) params.language = language;
      if (domain) params.domain = domain;
      const resp = await axios.get<ListResponse>(`${API_BASE}/admin/feedback`, {
        headers: getAuthHeaders(),
        params,
      });
      setRows(resp.data.items);
      setTotal(resp.data.total);
      setPages(resp.data.pages);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, rating, language, domain]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    // We fetch as a Blob so the bearer token gets attached. A naked <a> tag
    // cannot carry an Authorization header, so we round-trip through axios.
    try {
      const params: Record<string, string | number> = {};
      if (rating === 'up') params.rating = 1;
      if (rating === 'down') params.rating = -1;
      if (language) params.language = language;
      if (domain) params.domain = domain;
      const resp = await axios.get(`${API_BASE}/admin/feedback/export.csv`, {
        headers: { ...getAuthHeaders(), Accept: 'text/csv' },
        params,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to download CSV');
    }
  };

  if (!isSuperuser()) {
    return (
      <PageContainer title="Feedback">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
          Superuser privileges required to view feedback.
        </div>
      </PageContainer>
    );
  }

  // Authentication sanity check: if the token is missing we'd just get a 401
  // and the global interceptor would kick the user to /login. No silent UX.
  if (!getToken()) return null;

  return (
    <PageContainer
      title="Feedback"
      subtitle="Thumbs up / down ratings from the chat widget. Download as CSV for fine-tuning corpora."
      actions={
        <button onClick={downloadCsv} className="btn-primary">
          Download CSV
        </button>
      }
    >
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="eyebrow block mb-1">Rating</label>
          <select
            className="input-base text-sm"
            value={rating}
            onChange={(e) => { setPage(1); setRating(e.target.value as any); }}
          >
            <option value="">All</option>
            <option value="up">Helpful</option>
            <option value="down">Not helpful</option>
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Language</label>
          <select
            className="input-base text-sm"
            value={language}
            onChange={(e) => { setPage(1); setLanguage(e.target.value); }}
          >
            <option value="">All</option>
            <option value="ar">Arabic</option>
            <option value="en">English</option>
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Domain</label>
          <input
            className="input-base text-sm"
            placeholder="domain slug"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
          />
        </div>
        <div className="ml-auto text-sm text-primary-500">
          {total} {total === 1 ? 'rating' : 'ratings'}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-primary-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-primary-50 text-primary-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">When</th>
              <th className="text-left px-4 py-3 font-semibold">Rating</th>
              <th className="text-left px-4 py-3 font-semibold">Question</th>
              <th className="text-left px-4 py-3 font-semibold">Domain</th>
              <th className="text-left px-4 py-3 font-semibold">Lang</th>
              <th className="text-left px-4 py-3 font-semibold">Comment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">No ratings match the filters.</td></tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-primary-50 cursor-pointer transition-colors"
                  onClick={() => setExpanded(r)}
                >
                  <td className="px-4 py-3 text-primary-700 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                  </td>
                  <td className="px-4 py-3">
                    {r.rating === 1 ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium ring-1 ring-emerald-600/20">👍 Helpful</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium ring-1 ring-red-600/20">👎 Not helpful</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-primary-900 max-w-md truncate" title={r.query || ''}>
                    {r.query || <span className="text-primary-400">(no query)</span>}
                  </td>
                  <td className="px-4 py-3 text-primary-700 whitespace-nowrap">
                    {r.domain || <span className="text-primary-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-primary-700">{r.language || '—'}</td>
                  <td className="px-4 py-3 text-primary-700 max-w-xs truncate" title={r.comment || ''}>
                    {r.comment || <span className="text-primary-400">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-primary-600">
          <div>Page {page} of {pages}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="btn-secondary text-xs">Previous</button>
            <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} className="btn-secondary text-xs">Next</button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {expanded && <FeedbackDetailModal row={expanded} onClose={() => setExpanded(null)} />}
    </PageContainer>
  );
};

const FeedbackDetailModal: React.FC<{ row: FeedbackRow; onClose: () => void }> = ({ row, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-primary-200 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="eyebrow mb-1">Rating</div>
            <div className="text-sm text-primary-700">
              {row.rating === 1 ? '👍 Helpful' : '👎 Not helpful'}
              <span className="ml-3 font-mono text-xs text-primary-500">{row.session_id}</span>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs">Close</button>
        </div>
        <div className="px-6 py-5 overflow-y-auto space-y-4">
          <Field label="When" value={row.created_at ? new Date(row.created_at).toLocaleString() : '—'} />
          <Field label="Domain" value={row.domain || '—'} />
          <Field label="Language" value={row.language || '—'} />
          {row.intent && <Field label="Intent" value={`${row.intent}${row.confidence != null ? ` (${row.confidence.toFixed(2)})` : ''}`} />}
          {row.tool_calls_made && row.tool_calls_made.length > 0 && (
            <Field label="Tools called" value={row.tool_calls_made.join(', ')} />
          )}
          {(row.finish_reason || row.duration_ms != null || row.tokens_used != null) && (
            <Field label="Run" value={[
              row.finish_reason && `finish=${row.finish_reason}`,
              row.duration_ms != null && `${row.duration_ms} ms`,
              row.tokens_used != null && row.tokens_used > 0 && `${row.tokens_used} tokens`,
            ].filter(Boolean).join(' · ') || '—'} />
          )}
          <Field label="Question" value={row.query || '—'} multiline />
          <Field label="Answer" value={row.answer || '—'} multiline />
          {row.comment && <Field label="Comment" value={row.comment} multiline />}
          {row.citations && row.citations.length > 0 && (
            <div>
              <div className="eyebrow mb-2">Citations shown</div>
              <ul className="space-y-1.5">
                {row.citations.map((c, i) => (
                  <li key={i} className="text-sm text-primary-700">
                    <span className="font-mono text-xs text-primary-400 mr-2">{i + 1}</span>
                    {c.title || '(untitled)'}
                    {c.document_id && (
                      <a
                        href={`/doc/${c.document_id}${c.chunk_id ? `#chunk-${c.chunk_id}` : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-xs text-primary-700 hover:underline"
                      >
                        open
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; multiline?: boolean }> = ({ label, value, multiline }) => (
  <div>
    <div className="eyebrow mb-1">{label}</div>
    <div className={`text-sm text-primary-800 ${multiline ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>{value}</div>
  </div>
);

export default Feedback;
